import {
  Button,
  Flex,
  Heading,
  IconAddLine,
  IconButton,
  IconTrashLine,
  Text,
  ToggleGroup,
  Tooltip,
  View,
} from "@instructure/ui";
import { useTranslation } from "react-i18next";
import React, { useContext, useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { LearningOutcomesService } from "../../../services/LearningOutcomesService";
import { OutcomeCategoryService } from "../../../services/OutcomeCategoryService";
import { MasteryLevelContext } from "../../../utils/Context";
import { sort } from "../../../utils/utils";
import { Card } from "../../Card";
import { ERROR, useNotification } from "../../CustomHooks/NotificationProvider";
import { DialogYesNo } from "../../DialogYesNo/DialogYesNo";
import EditOutcomeModal from "../../Outcomes/Review/EditOutcomeModal";
import ArchiveToggle from "../ArchiveToggle";
import CategoriesModal from "../CategoriesModal/CategoriesModal";
import { isArchived, sortOutcomes } from "../utils";
import ILOCard from "./ILOCard";
import ILOUploadModal from "./ILOUploadModal";
import { getCurrentVersion } from "../utils";

export const InstitutionalLearningOutcome = () => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const { ratingScales } = useContext(MasteryLevelContext);
  const [isLoading, setIsLoading] = useState(true);

  const [categories, setCategories] = useState([]);
  const filterCategories = (categories) => {
    const unarchivedCategories = [];
    categories.forEach((category) => {
      const unarchivedLos = category.outcomes.filter((lo) => !isArchived(lo));

      if (category.outcomes.length === 0 || unarchivedLos.length > 0) {
        unarchivedCategories.push({ ...category, outcomes: unarchivedLos });
      }
    });
    return unarchivedCategories;
  };
  const unarchivedCategories = filterCategories(categories);

  const [uncategorizedLos, setUncategorizedLos] = useState([]);
  const unarchivedUncategorizedLos = uncategorizedLos.filter(
    (lo) => !isArchived(lo)
  );

  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState("");

  useEffect(() => {
    const fetchOutcomes = async () => {
      try {
        const iloPromises = [
          OutcomeCategoryService.getOutcomeCategoriesByContext("ISLO"),
          OutcomeCategoryService.getUncategorizedOutcomesByContext("ISLO"),
        ];
        const [categories, unc] = await Promise.all(iloPromises);

        categories.forEach((cat) => {
          cat.outcomes = sortOutcomes(cat.outcomes);
          cat.outcomes.forEach((o) => {
            o.category_id = cat.ddl_id;
          });
        });
        setCategories(sort(categories, (cat) => cat.name));

        setUncategorizedLos(
          sortOutcomes([
            ...unc.institutional_outcomes,
            ...(unc.unapproved_outcomes ?? []),
          ])
        );

        setIsLoading(false);
      } catch {
        showNotification(
          t("notifications.general.fetchError", { obj: t("setup.ilosLink") }),
          ERROR
        );
      }
    };
    if (isLoading) {
      fetchOutcomes();
    }
  }, [isLoading]);

  const handleCategoryDialog = (categoryId) => {
    setCategoryToDelete(categoryId);
    setOpenCategoryDialog(true);
  };

  const handleDeleteCategory = async () => {
    await OutcomeCategoryService.deleteOutcomeCategory(categoryToDelete)
      .then(() => {
        setOpenCategoryDialog(false);
        setCategoryToDelete("");
        setIsLoading(true);
      })
      .catch(() => {
        showNotification(
          t("notifications.general.deleteError", {
            obj: t("notifications.category.obj"),
          }),
          ERROR
        );
      });
  };

  const handleNoClick = () => {
    setCategoryToDelete("");
    setOpenCategoryDialog(false);
  };

  const handleSaveOutcome = async (newName, newStatement, outcomeVariety) => {
    try {
      const newOutcome = await LearningOutcomesService.createLearningOutcome({
        name: newName,
        seq: uncategorizedLos.length + 1,
        statement: newStatement,
        outcomeType: "ISLO",
        outcomeVariety: outcomeVariety,
      });
      setUncategorizedLos((prevLos) => sortOutcomes([...prevLos, newOutcome]));
      showNotification(
        t("notifications.general.createSuccess", {
          obj: t("ilo.institutionalLearningOutcome"),
        })
      );
    } catch {
      throw new Error(
        t("notifications.general.createError", {
          obj: t("ilo.institutionalLearningOutcome"),
        })
      );
    }
  };

  const deleteOutcome = async (outcomeToDelete) => {
    await LearningOutcomesService.deleteLearningOutcome(outcomeToDelete, "ISLO")
      .then(() => {
        for (let i = 0; i < categories.length; i++) {
          if (categories[i].outcomes && categories[i].outcomes.length > 0) {
            setCategories((prevCategories) => {
              prevCategories[i].outcomes = prevCategories[i].outcomes.filter(
                (o) => o.id !== outcomeToDelete.id
              );
              return [...prevCategories];
            });
          }
        }
        setUncategorizedLos((prevLos) => {
          prevLos = prevLos.filter((o) => o.id !== outcomeToDelete.id);
          return [...prevLos];
        });
        showNotification(t("notifications.ilo.deleteSuccess"));
      })
      .catch((e) =>
        showNotification(`${t("notifications.ilo.deleteError")} ${e}`, ERROR)
      );
  };

  const handleArchiveOutcome = (outcome, term) => {
    setUncategorizedLos((prevLos) =>
      prevLos.map((lo) => {
        if (lo.id === outcome.id) {
          lo = getCurrentVersion(lo);
          lo.archive_term = term;
        }
        return lo;
      })
    );
    setCategories((prevCategories) =>
      prevCategories.map((cat) => {
        if (cat.outcomes && cat.outcomes.length > 0) {
          cat.outcomes = cat.outcomes.map((lo) => {
            if (lo.id === outcome.id) {
              lo = getCurrentVersion(lo);
              lo.archive_term = term;
            }
            return lo;
          });
        }
        return cat;
      })
    );
  };

  const handleUpdateRatingScale = (newLo) => {
    setUncategorizedLos((prevLos) =>
      prevLos.map((lo) => {
        if (lo.id === newLo.id) {
          lo.rating_scales = newLo.rating_scales;
        }
        return lo;
      })
    );
    setCategories((prevCategories) =>
      prevCategories.map((cat) => {
        if (cat.outcomes && cat.outcomes.length > 0) {
          cat.outcomes = cat.outcomes.map((lo) => {
            if (lo.id === newLo.id) {
              lo.rating_scales = newLo.rating_scales;
            }
            return lo;
          });
        }
        return cat;
      })
    );
  };

  return (
    <>
      <div style={{ marginBottom: "15px", borderBottom: "1px solid #ccc" }}>
        <Flex alignItems="end" gap="small" wrap="wrap">
          <Flex.Item shouldGrow>
            <Heading as="h2" level="h3">
              {t("ilo.heading")}
            </Heading>
          </Flex.Item>
          {/* <Flex.Item>
            {!isLoading && (
              <Heading color="secondary" level="h4">
                {outcomeList.length === 0 ? "No" : outcomeList.length} Outcome
                {outcomeList.length > 1 && "s"}
              </Heading>
            )}
          </Flex.Item> */}
          <Flex.Item>
            <AddOutcomeButton
              outcome={{
                name: "",
                statement: "",
                sequence: uncategorizedLos.length + 1,
                outcome_variety: "outcome",
              }}
              handleSave={handleSaveOutcome}
            />
          </Flex.Item>
          <Flex.Item>
            <CategoriesModal
              data={uncategorizedLos}
              loading={isLoading}
              setLoader={setIsLoading}
              title={t("categories.addIloCategory")}
              loType="ISLO"
              service={(category) =>
                OutcomeCategoryService.createOutcomeCategory(category)
              }
              selected={false}
              buttonText={t("global.addCategory")}
              type={"add"}
            />
          </Flex.Item>
          <Flex.Item>
            <ILOUploadModal
              uncategorizedLos={uncategorizedLos}
              categories={categories}
            />
          </Flex.Item>
        </Flex>
      </div>

      {!ratingScales || isLoading ? (
        <Skeleton
          count={3}
          style={{ height: 106, borderRadius: 5, marginBottom: 10 }}
        />
      ) : (
        <Flex wrap="wrap" gap="small">
          <Flex.Item size="100%">
            <ArchiveToggle
              label={t("learningOutcomes.archive.archivedILOs")}
              uncategorizedLos={uncategorizedLos}
              categories={categories}
            />
          </Flex.Item>
          {unarchivedUncategorizedLos.length > 0 && (
            <Flex.Item size="100%">
              <ToggleGroup
                defaultExpanded
                toggleLabel={t("categories.uncategorized")}
                summary={t("categories.uncategorized")}
              >
                {unarchivedUncategorizedLos.map((outcome) => (
                  <Card
                    key={outcome.id}
                    style={{
                      color: "#0D323F",
                      backgroundColor: "#F2F8FA",
                      border: "1px solid #ccc",
                      borderRadius: 10,
                      padding: "5px 15px 15px 15px",
                      margin: 15,
                    }}
                  >
                    <ILOCard
                      outcome={outcome}
                      categories={categories}
                      uncategorizedLos={uncategorizedLos}
                      deleteOutcome={deleteOutcome}
                      setIsLoading={setIsLoading}
                      handleArchiveOutcome={handleArchiveOutcome}
                      handleUpdateRatingScale={handleUpdateRatingScale}
                    />
                  </Card>
                ))}
              </ToggleGroup>
            </Flex.Item>
          )}
          {unarchivedCategories.map((category, key) => (
            <Flex.Item key={key} size="100%">
              <ToggleGroup
                defaultExpanded
                toggleLabel={category.name}
                summary={
                  <Flex gap="small">
                    <Flex.Item shouldGrow shouldShrink>
                      <Text>{category.name} </Text>
                      <Text fontStyle="italic">
                        {"(" + category.description + ")"}
                      </Text>
                    </Flex.Item>
                    <Flex.Item>
                      <CategoriesModal
                        data={
                          category.outcomes
                            ? [...uncategorizedLos].concat([
                                ...category.outcomes,
                              ])
                            : [...uncategorizedLos]
                        }
                        loading={isLoading}
                        setLoader={setIsLoading}
                        title={t("categories.editIloCategory")}
                        loType="ISLO"
                        service={(ncategory) => {
                          ncategory.id = category.ddl_id;
                          return OutcomeCategoryService.updateOutcomeCategory(
                            ncategory
                          );
                        }}
                        selected={{ ...category }}
                        category={category}
                        type={"edit"}
                      />
                    </Flex.Item>
                    <Flex.Item>
                      <Tooltip renderTip={t("global.deleteCategory")}>
                        <IconButton
                          renderIcon={IconTrashLine}
                          screenReaderLabel={t("global.deleteCategory")}
                          onClick={() =>
                            handleCategoryDialog(category.ddl_id ?? category.id)
                          }
                        />
                      </Tooltip>
                    </Flex.Item>
                  </Flex>
                }
              >
                {category?.outcomes?.length === 0 ? (
                  <View as="p" padding="small">
                    {t("ilo.noOutcomes")}
                  </View>
                ) : (
                  category?.outcomes.map((outcome, index) => (
                    <Card
                      key={index}
                      style={{
                        color: "#0D323F",
                        backgroundColor: "#F2F8FA",
                        border: "1px solid #ccc",
                        borderRadius: 10,
                        padding: "5px 15px 15px 15px",
                        margin: 15,
                      }}
                    >
                      <ILOCard
                        outcome={outcome}
                        categories={categories}
                        uncategorizedLos={uncategorizedLos}
                        deleteOutcome={deleteOutcome}
                        setIsLoading={setIsLoading}
                        handleArchiveOutcome={handleArchiveOutcome}
                        handleUpdateRatingScale={handleUpdateRatingScale}
                      />
                    </Card>
                  ))
                )}
              </ToggleGroup>
            </Flex.Item>
          ))}
        </Flex>
      )}
      <DialogYesNo
        message={t("ilo.deleteCategoryMessage")}
        openStatus={openCategoryDialog}
        onClickNo={handleNoClick}
        onClickYes={handleDeleteCategory}
      />
    </>
  );
};

const AddOutcomeButton = ({ outcome, handleSave }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        id="add-outcome-button"
        color="primary"
        renderIcon={IconAddLine}
        onClick={() => setOpen(true)}
      >
        {t("ilo.addButton")}
      </Button>
      <EditOutcomeModal
        open={open}
        setOpen={setOpen}
        outcome={outcome}
        handleSave={handleSave}
      />
    </>
  );
};
